import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Users, Target, DollarSign, Trophy, TrendingUp, Clock, Banknote, AlertTriangle, BarChart3, PieChart } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

const formatCLP = (amount: number) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(amount || 0);

function getPresetDates(preset: string) {
  const now = new Date();
  const to = now.toISOString().split('T')[0];
  if (preset === '7d') {
    const d = new Date(now); d.setDate(d.getDate() - 7);
    return { from: d.toISOString().split('T')[0], to };
  }
  if (preset === '30d') {
    const d = new Date(now); d.setDate(d.getDate() - 30);
    return { from: d.toISOString().split('T')[0], to };
  }
  if (preset === '90d') {
    const d = new Date(now); d.setDate(d.getDate() - 90);
    return { from: d.toISOString().split('T')[0], to };
  }
  return { from: '', to: '' };
}

function withinRange(value?: string, from?: string, to?: string) {
  if (!value) return true;
  const day = value.slice(0, 10);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

const metricMeta: Record<string, { title: string; description: string; icon: any }> = {
  leads: { title: 'Leads Capturados', description: 'Explora los leads que ingresaron al embudo.', icon: Users },
  opportunities: { title: 'Oportunidades Activas', description: 'Ver pipeline y oportunidades en curso.', icon: Target },
  pipeline: { title: 'Valor del Pipeline', description: 'Detalle del valor por etapa.', icon: DollarSign },
  won: { title: 'Negocios Ganados', description: 'Ventas cerradas y su historia.', icon: Trophy },
  conversion: { title: 'Tasa de Conversión', description: 'Relación entre leads y negocios ganados.', icon: TrendingUp },
  activities: { title: 'Actividades Pendientes', description: 'Tareas, reuniones y llamadas abiertas.', icon: Clock },
  plata: { title: 'Plata en la Calle', description: 'Monto en oportunidades no cerradas.', icon: Banknote },
  inactivity: { title: 'Alertas de Inactividad', description: 'Cuentas o contactos sin movimiento.', icon: AlertTriangle },
  risk: { title: 'Oportunidades en Riesgo', description: 'Oportunidades con salud comercial bajo 80.', icon: AlertTriangle },
  no_next: { title: 'Sin Próxima Actividad', description: 'Oportunidades abiertas sin próximo paso o con seguimiento vencido.', icon: AlertTriangle },
  leads_overdue: { title: 'Leads sin Gestionar (24h)', description: 'Leads nuevos sin contacto dentro de 24 horas.', icon: AlertTriangle },
  stage: { title: 'Pipeline por Etapa', description: 'Desglose de oportunidades por etapa.', icon: BarChart3 },
  source: { title: 'Leads por Fuente', description: 'Origen de los leads capturados.', icon: PieChart },
};

const leadStatusColors: Record<string, string> = {
  nuevo: 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20',
  contactado: 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  calificado: 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  descartado: 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
  convertido: 'bg-[#F5F3FF] text-[#7C3AED] border-[#8B5CF6]/20',
};

const leadSourceLabels: Record<string, string> = { web: 'Web', referido: 'Referido', evento: 'Evento', linkedin: 'LinkedIn', whatsapp: 'WhatsApp', email: 'Email' };
const oppStageColors: Record<string, string> = {
  Prospecto: 'bg-[#F1F5F9] text-[#475569] border-[#94A3B8]/30',
  'Contacto realizado': 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20',
  'Reunion / demo': 'bg-[#ECFEFF] text-[#0F766E] border-[#14B8A6]/20',
  'Propuesta enviada': 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  'Negociacion': 'bg-[#F5F3FF] text-[#7C3AED] border-[#8B5CF6]/20',
  'Cerrado ganado': 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  'Cerrado perdido': 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
};

export default function DashboardDrilldownPage() {
  const { metric = 'leads' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentOrgId } = useOrgStore();
  const [payload, setPayload] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [from, setFrom] = useState(searchParams.get('from') || '');
  const [to, setTo] = useState(searchParams.get('to') || '');

  useEffect(() => {
    const load = async () => {
      if (!currentOrgId) return;
      const params: any = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const [kpisRes, chartsRes, alertsRes, leadsRes, oppsRes, activitiesRes, companiesRes] = await Promise.all([
        api.get('/dashboard/kpis', { params }),
        api.get('/dashboard/charts', { params }),
        api.get('/dashboard/alerts', { params }),
        api.get('/leads', { params }),
        api.get('/opportunities', { params }),
        api.get('/activities', { params }),
        api.get('/companies'),
      ]);
      setPayload({
        kpis: kpisRes.data,
        charts: chartsRes.data,
        alerts: alertsRes.data,
        leads: leadsRes.data,
        opportunities: oppsRes.data,
        activities: activitiesRes.data,
      });
      setCompanies(companiesRes.data || []);
    };
    load().catch(console.error);
  }, [currentOrgId, from, to]);

  useEffect(() => {
    const next: any = {};
    if (from) next.from = from;
    if (to) next.to = to;
    setSearchParams(next, { replace: true });
  }, [from, to, setSearchParams]);

  const meta = metricMeta[metric] || metricMeta.leads;
  const Icon = meta.icon;

  const data = useMemo(() => {
    const leads = payload?.leads || [];
    const opportunities = payload?.opportunities || [];
    const activities = payload?.activities || [];
    const alerts = payload?.alerts || [];
    const chartStages = payload?.charts?.pipeline_by_stage || [];
    const chartSources = payload?.charts?.leads_by_source || [];

    if (metric === 'leads') return leads.filter((item: any) => withinRange(item.created_at, from, to));
    if (metric === 'opportunities') return opportunities.filter((item: any) => withinRange(item.created_at, from, to) || withinRange(item.updated_at, from, to));
    if (metric === 'risk') {
      const riskyIds = new Set(alerts.filter((a: any) => a.type === 'oportunidad_en_riesgo').map((a: any) => a.entity_id));
      return opportunities.filter((item: any) => riskyIds.has(item.id));
    }
    if (metric === 'no_next') {
      const ids = new Set(alerts.filter((a: any) => ['oportunidad_sin_proximo_paso', 'seguimiento_vencido'].includes(a.type)).map((a: any) => a.entity_id));
      return opportunities.filter((item: any) => ids.has(item.id));
    }
    if (metric === 'leads_overdue') {
      const ids = new Set(alerts.filter((a: any) => a.type === 'lead_sin_contacto_24h').map((a: any) => a.entity_id));
      return leads.filter((item: any) => ids.has(item.id));
    }
    if (metric === 'pipeline') return chartStages;
    if (metric === 'won') return opportunities.filter((item: any) => item.stage === 'Cerrado ganado');
    if (metric === 'conversion') return opportunities;
    if (metric === 'activities') return activities.filter((item: any) => item.status === 'pendiente');
    if (metric === 'plata') return opportunities.filter((item: any) => !['Cerrado ganado', 'Cerrado perdido'].includes(item.stage));
    if (metric === 'inactivity') return activities.filter((item: any) => item.status === 'pendiente');
    if (metric === 'stage') return chartStages;
    if (metric === 'source') return chartSources;
    return [];
  }, [payload, metric, from, to]);

  const leadRows = useMemo(() => {
    if (metric !== 'leads') return [];
    return data.map((item: any) => ({
      ...item,
      sourceLabel: leadSourceLabels[item.source] || item.source,
    }));
  }, [data, metric]);

  const opportunityRows = useMemo(() => {
    if (metric !== 'opportunities' && metric !== 'pipeline' && metric !== 'won' && metric !== 'plata') return [];
    return data.map((item: any) => ({
      ...item,
      totalValue: Number(item.total_bruto || item.amount || 0),
      companyName: companies.find((company) => company.id === item.account_id)?.name || item.company || 'Sin empresa',
    }));
  }, [data, metric, companies]);

  const timeline = useMemo(() => {
    const leads = payload?.leads || [];
    const opportunities = payload?.opportunities || [];
    const activities = payload?.activities || [];
    return [
      ...leads.map((item: any) => ({ id: item.id, date: item.created_at, title: `Lead ${item.name}`, detail: item.company || item.source || 'Lead capturado', type: 'lead' })),
      ...opportunities.map((item: any) => ({ id: item.id, date: item.updated_at || item.created_at, title: `Oportunidad ${item.name}`, detail: `${item.stage} · ${formatCLP(item.total_bruto || item.amount || 0)}`, type: 'opp' })),
      ...activities.map((item: any) => ({ id: item.id, date: item.due_date || item.created_at, title: `Actividad ${item.subject}`, detail: `${item.type} · ${item.status}`, type: 'activity' })),
    ]
      .filter((item) => withinRange(item.date, from, to))
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
      .slice(0, 12);
  }, [payload, from, to]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="outline" className="border-[#E2E8F0]">
            <Link to="/"><ArrowLeft size={16} className="mr-2" /> Volver</Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {meta.title}
            </h1>
            <p className="text-sm text-[#475569] mt-1">{meta.description}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { const d = getPresetDates('7d'); setFrom(d.from); setTo(d.to); }} className="min-h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0">7d</Button>
          <Button variant="outline" size="sm" onClick={() => { const d = getPresetDates('30d'); setFrom(d.from); setTo(d.to); }} className="min-h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0">30d</Button>
          <Button variant="outline" size="sm" onClick={() => { const d = getPresetDates('90d'); setFrom(d.from); setTo(d.to); }} className="min-h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0">90d</Button>
          <Button variant="outline" size="sm" onClick={() => { setFrom(''); setTo(''); }} className="min-h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0">Limpiar</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon size={18} />
            Resumen del bloque
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="p-4 rounded-lg border border-[#E2E8F0]"><p className="text-xs uppercase tracking-widest text-[#94A3B8]">Desde</p><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-2" /></div>
          <div className="p-4 rounded-lg border border-[#E2E8F0]"><p className="text-xs uppercase tracking-widest text-[#94A3B8]">Hasta</p><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-2" /></div>
          <div className="p-4 rounded-lg border border-[#E2E8F0]"><p className="text-xs uppercase tracking-widest text-[#94A3B8]">Items</p><p className="mt-2 text-2xl font-bold tabular-nums">{data.length}</p></div>
          <div className="p-4 rounded-lg border border-[#E2E8F0]"><p className="text-xs uppercase tracking-widest text-[#94A3B8]">Vista</p><p className="mt-2 text-lg font-semibold break-words">{metric}</p></div>
        </CardContent>
      </Card>

      {metric === 'leads' ? (
        <Card className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#2563EB] via-[#8B5CF6] to-[#22C55E]" />
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#0F172A]">Leads capturados</CardTitle>
            <p className="text-xs text-[#64748B]">Tabla operativa alineada con la vista principal de leads.</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Nombre</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Email</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden md:table-cell">Empresa</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden lg:table-cell">Fuente</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Estado</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {leadRows.map((lead: any) => (
                    <tr
                      key={lead.id}
                      className="border-b border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors"
                    >
                      <td className="py-3 px-4 font-medium text-[#0F172A]">
                        <button
                          type="button"
                          className="rounded-sm text-left hover:text-[#2563EB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                          onClick={() => navigate('/leads')}
                        >
                          {lead.name}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-[#475569]">{lead.email}</td>
                      <td className="py-3 px-4 text-[#475569] hidden md:table-cell">{lead.company}</td>
                      <td className="py-3 px-4 text-[#475569] hidden lg:table-cell">{lead.sourceLabel}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={`text-xs font-medium capitalize ${leadStatusColors[lead.status] || ''}`}>{lead.status}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate('/leads')}>
                              <Pencil size={14} className="mr-2" /> Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/leads')} className="text-[#EF4444]">
                              <Trash2 size={14} className="mr-2" /> Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {leadRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-[#94A3B8]">No hay leads registrados</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <ul className="space-y-3 p-4 lg:hidden">
              {leadRows.map((lead: any) => (
                <li key={lead.id} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      className="min-w-0 rounded-sm text-left font-semibold text-[#0F172A] break-words hover:text-[#2563EB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                      onClick={() => navigate('/leads')}
                    >
                      {lead.name}
                    </button>
                    <Badge variant="outline" className={`shrink-0 text-xs font-medium capitalize ${leadStatusColors[lead.status] || ''}`}>{lead.status}</Badge>
                  </div>
                  <dl className="mt-2 grid grid-cols-3 gap-y-1 text-sm">
                    <dt className="text-[#64748B]">Email</dt>
                    <dd className="col-span-2 break-words text-right text-[#475569]">{lead.email || '-'}</dd>
                    <dt className="text-[#64748B]">Empresa</dt>
                    <dd className="col-span-2 break-words text-right text-[#475569]">{lead.company || '-'}</dd>
                    <dt className="text-[#64748B]">Fuente</dt>
                    <dd className="col-span-2 text-right text-[#475569]">{lead.sourceLabel || '-'}</dd>
                  </dl>
                </li>
              ))}
              {leadRows.length === 0 && (
                <li className="py-12 text-center text-[#94A3B8]">No hay leads registrados</li>
              )}
            </ul>
          </CardContent>
        </Card>
      ) : metric === 'opportunities' || metric === 'pipeline' || metric === 'won' || metric === 'plata' ? (
        <Card className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#8B5CF6] via-[#F59E0B] to-[#22C55E]" />
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#0F172A]">Oportunidades</CardTitle>
            <p className="text-xs text-[#64748B]">Tabla operativa con enlace directo al detalle y timeline de etapa.</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Nombre</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden md:table-cell">Empresa</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] text-right">Neto</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] text-right hidden lg:table-cell">IVA</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] text-right">Total Bruto</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Etapa</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden lg:table-cell">Cierre</th>
                    <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {opportunityRows.map((opp: any) => (
                    <tr key={opp.id} className="border-b border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors">
                      <td className="py-3 px-4 font-medium text-[#0F172A]">
                        <button type="button" className="rounded-sm text-left hover:text-[#2563EB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2" onClick={() => navigate(`/oportunidades/${opp.id}`)}>
                          {opp.name}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-[#475569] hidden md:table-cell">{opp.companyName}</td>
                      <td className="py-3 px-4 text-right tabular-nums text-[#475569]">{formatCLP(opp.monto_neto || 0)}</td>
                      <td className="py-3 px-4 text-right tabular-nums text-[#475569] hidden lg:table-cell">{opp.exento ? 'Exento' : formatCLP(opp.iva || 0)}</td>
                      <td className="py-3 px-4 text-right font-semibold tabular-nums text-[#0F172A]">{formatCLP(opp.totalValue)}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={`text-xs font-medium ${oppStageColors[opp.stage] || ''}`}>{opp.stage}</Badge>
                      </td>
                      <td className="py-3 px-4 text-[#475569] hidden lg:table-cell">{opp.close_date || '-'}</td>
                      <td className="py-3 px-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/oportunidades/${opp.id}`)}>
                              <Pencil size={14} className="mr-2" /> Ver detalle
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/oportunidades')} className="text-[#EF4444]">
                              <Trash2 size={14} className="mr-2" /> Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {opportunityRows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-[#94A3B8]">No hay oportunidades registradas</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <ul className="space-y-3 p-4 lg:hidden">
              {opportunityRows.map((opp: any) => (
                <li key={opp.id} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <button type="button" className="min-w-0 rounded-sm text-left font-semibold text-[#0F172A] break-words hover:text-[#2563EB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2" onClick={() => navigate(`/oportunidades/${opp.id}`)}>
                      {opp.name}
                    </button>
                    <Badge variant="outline" className={`shrink-0 text-xs font-medium ${oppStageColors[opp.stage] || ''}`}>{opp.stage}</Badge>
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-y-1 text-sm">
                    <dt className="text-[#64748B]">Empresa</dt>
                    <dd className="break-words text-right text-[#475569]">{opp.companyName}</dd>
                    <dt className="text-[#64748B]">Neto</dt>
                    <dd className="text-right tabular-nums text-[#475569]">{formatCLP(opp.monto_neto || 0)}</dd>
                    <dt className="text-[#64748B]">IVA</dt>
                    <dd className="text-right tabular-nums text-[#475569]">{opp.exento ? 'Exento' : formatCLP(opp.iva || 0)}</dd>
                    <dt className="text-[#64748B]">Total Bruto</dt>
                    <dd className="text-right font-semibold tabular-nums text-[#0F172A]">{formatCLP(opp.totalValue)}</dd>
                    <dt className="text-[#64748B]">Cierre</dt>
                    <dd className="text-right text-[#475569]">{opp.close_date || '-'}</dd>
                  </dl>
                </li>
              ))}
              {opportunityRows.length === 0 && (
                <li className="py-12 text-center text-[#94A3B8]">No hay oportunidades registradas</li>
              )}
            </ul>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.map((item: any) => (
            <Card
              key={item.id || item._id}
              className="cursor-pointer hover:shadow-md transition-shadow bg-white/95 backdrop-blur"
              onClick={() => {
                if (metric === 'activities' || metric === 'inactivity') navigate('/actividades');
                if (metric === 'source' || metric === 'stage' || metric === 'conversion') navigate('/');
              }}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{item.name || item._id || 'Detalle'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm text-[#475569]">
                {'count' in item && <p>{item.count} registros</p>}
                {'subject' in item && <p>{item.subject}</p>}
                {'_id' in item && <p>{item._id}</p>}
                {metric === 'source' && <p className="text-xs text-[#94A3B8]">Click para volver al Dashboard</p>}
                {metric === 'stage' && <p className="text-xs text-[#94A3B8]">Click para abrir el pipeline</p>}
              </CardContent>
            </Card>
          ))}
          {data.length === 0 && (
            <div className="text-sm text-[#94A3B8]">No hay datos en este rango.</div>
          )}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Línea de tiempo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {timeline.map((item) => (
            <div key={item.id} className="flex items-start justify-between gap-4 p-3 rounded-lg border border-[#E2E8F0]">
              <div>
                <p className="font-medium text-[#0F172A]">{item.title}</p>
                <p className="text-xs text-[#475569]">{item.detail}</p>
              </div>
              <Badge variant="outline" className="text-xs">{item.type}</Badge>
            </div>
          ))}
          {timeline.length === 0 && <p className="text-sm text-[#94A3B8]">Sin eventos en este rango.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
